/*Copyright © SITA INC Ireland, SITA INC USA, SITA Switzerland Sarl 2025. Confidential. All rights reserved.*/
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { catchError, map } from 'rxjs';
import {
  HttpClient,
} from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Constants } from '../constants';
import { LoadingService } from './loading.service';
import { TimeoutComponent } from '../../shared/timeout/timeout.component';
import { StorageService } from './storage.service';

export interface SessionData {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  isAdmin: boolean;
  customer_name: string;
  role: string;
  username?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SessionmanagementService {
  private readonly TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN = 'refresh_token';
  private readonly USER_DATA_KEY = 'USER_Data';
  private SESSION_DURATION = 30 * 60 * 1000;
  private readonly timeIntervalArr = <any>[];
  public getTimeIntervalArr() {
    return this.timeIntervalArr;
  }

  public setTimeIntervalArr(interval: any) {
    this.timeIntervalArr.push(interval);
  }

  isAuthenticated = false;
  authToken: string | null = '';
  isAdmin = false;
  private _customerName: string | null = null;
  private _username: string | null = null;
  private _userRole: string | null = null;
  public get userRole() {
    return this._userRole;
  }
  public set userRole(value) {
    this._userRole = value;
  }
  public get customerName() {
    return this._customerName;
  }
  public set customerName(value) {
    this._customerName = value;
  }
  public get username() {
    return this._username;
  }
  public set username(value) {
    this._username = value;
  }


  constructor(
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly httpSrvc: HttpClient,
    private readonly storageService: StorageService,
    private readonly loader: LoadingService) {
    let userIsAdmin: any = this.storageService.getStorageItem('ADMIN', false);
    if (userIsAdmin === 'true') {
      this.setAdmin(true);
    }

    window.addEventListener('storage', (event) => {
      if (event.key === this.TOKEN_KEY && event.newValue === null) {
        this.logout();
      }
    })
  }

  // Session management methods
  public setUserSession(user: SessionData | any): void {
    if (user.access_token) {
      const data: SessionData = {
        access_token: user.access_token,
        refresh_token: user.refresh_token,
        token_type: user.token_type,
        expires_in: user.expires_in,
        isAdmin: user.isAdmin,
        customer_name: user.customer_name,
        role: user.role,
        username: user.username
      };
      // Store both fields separately
      this.customerName = user.customer_name || null;
      this.username = user.username || null;
      // userName can be used as a fallback (prioritize username if available)
      this.userRole = user.role;
      this.SESSION_DURATION = user.expires_in;
      this.isAdmin = user.isAdmin;
      this.storageService.setStorageItem("ADMIN", String(this.isAdmin), false);
      this.authToken = user.access_token;
      this.setStorageItem(this.TOKEN_KEY, user.access_token);
      this.setStorageItem(this.REFRESH_TOKEN, user.refresh_token);
      this.setStorageItem(this.USER_DATA_KEY, JSON.stringify(data));
    } else {
      this.isAuthenticated = false;
    }
  }

  refreshToken() {
    return this.httpSrvc
      .post<any>(environment.apiUrl + Constants.REFRESH_URI, null)
      .pipe(
        map((response) => {
          if (response && response.access_token) {
            this.setStorageItem(this.TOKEN_KEY, response.access_token);
          }
          return response
        }),
        catchError((error) => {
          this.openTimeout();
          throw error;
        }));
  }

  private setStorageItem(key: string, value: string): void {
    this.storageService.setStorageItem(key, value);
  }

  private removeStorageItem(key: string): void {
    this.storageService.removeStorageItem(key);
  }




  isLoggedIn(): boolean {
    this.authToken = this.getToken();
    return !!this.authToken;
  }

  getToken(): string | null {
    return this.storageService.getStorageItem(this.TOKEN_KEY);
  }

  getUserData(): SessionData | null {
    const userDataString = this.storageService.getStorageItem(this.USER_DATA_KEY);
    if (userDataString) {
      try {
        return JSON.parse(userDataString) as SessionData;
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    }
    return null;
  }

  logout(isSessionExp: boolean = false): void {
    this.removeStorageItem(this.TOKEN_KEY);
    this.removeStorageItem(this.REFRESH_TOKEN);
    this.removeStorageItem(this.USER_DATA_KEY);
    this.clearSessionStorage();
    this.timeIntervalArr.forEach((element: any) => {
      window.clearInterval(element)
    });
    this.closeOpenDialog();
    this.loader.hideSpinner();
    this.router.navigate(['']);
  }

  openTimeout(data?: { title: string, bodyText: string }) {
    this.loader.hideSpinner();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.height = 'fit-content';
    dialogConfig.width = '360px';
    dialogConfig.disableClose = true;
    dialogConfig.data = data;
    this.timeIntervalArr.forEach((element: any) => {
      window.clearInterval(element)
    });
    this.dialog.open(TimeoutComponent, dialogConfig);
  }

  setAdmin(isAdmin: boolean) {
    this.isAdmin = isAdmin;
  }

  closeOpenDialog() {
    let openDialog = this.dialog.openDialogs;
    if (openDialog && openDialog.length > 0)
      openDialog.forEach(popup => popup.close());
  }

  private clearSessionStorage(): void {
    sessionStorage.clear();
  }

  getAdmin() : boolean{
    return this.isAdmin
  }
}
